import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Ban, CheckCircle, DollarSign, RefreshCw, Store } from 'lucide-react';
import { AppLayout } from '@/components/layout/app-layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNativo } from '@/components/ui/select-nativo';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { useToast } from '@/lib/hooks/use-toast';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { prixiaService } from '../services/prixia-service';
import type {
  CambioDeCalendario,
  DetalleDeTiendaPrixia,
  EjeDeTiendas,
  EmpresaDeLaTienda,
  FilaDeLecturaPrixia,
  Lector,
  TipoDeLectura,
} from '../types/prixia.types';
import {
  SALUD,
  TIPO_DE_LECTURA,
  codigoDeMoneda,
  etiquetasDeLectores,
  fechaYHora,
  frecuenciaEnTexto,
} from '../lib/prixia-textos';
import { EditorDeCalendarioPrixia } from '../components/editor-de-calendario-prixia';
import { TablaDeLecturasPrixia } from '../components/tabla-de-lecturas-prixia';

const TIPOS: TipoDeLectura[] = ['PRICE_CHECK', 'FULL_RESCRAPE'];

const HORAS_POR_UNIDAD = { HOURS: 1, DAYS: 24, WEEKS: 168 } as const;

/** Cada cuántas horas se lee con esa frecuencia (para saber si se lee más seguido). */
function enHoras(valor: number, unidad: keyof typeof HORAS_POR_UNIDAD): number {
  return valor * HORAS_POR_UNIDAD[unidad];
}

/**
 * La copia que deja el servidor al fusionar (`fusion.ts`): entre las encendidas si hay
 * alguna (la fusión no le quita la tienda a la empresa), la de más barridos; empate, la
 * más vieja (vienen por antigüedad y el orden se mantiene).
 */
function copiaQueQueda(e: EmpresaDeLaTienda): {
  queda: EmpresaDeLaTienda['copias'][number] | undefined;
  encendida: boolean;
} {
  const vivas = e.copias.filter((c) => !c.fusionada);
  const encendidas = vivas.filter((c) => c.isActive);
  const candidatas = encendidas.length > 0 ? encendidas : vivas;
  const queda = [...candidatas].sort((a, b) => b.barridos - a.barridos)[0];
  return { queda, encendida: encendidas.length > 0 };
}

/**
 * La ficha de una tienda del catálogo de Prixia (plan 297): calendario, datos,
 * encender/apagar, pedir una lectura, qué empresas la tienen (y fusionar sus copias
 * repetidas) y sus últimas lecturas con quién las pidió (C10).
 */
export function AdminTiendaPrixiaPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [detalle, setDetalle] = useState<DetalleDeTiendaPrixia | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lectores, setLectores] = useState<Lector[]>([]);
  const [sinLectores, setSinLectores] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [datos, setDatos] = useState({
    name: '',
    scraperType: 'auto',
    logoUrl: '',
    visitUrl: '',
    moneda: '',
    ejeId: '',
  });
  // Reglas de la demo (343): los rubros que se pueden elegir (activos, más el que ya tiene).
  const [ejes, setEjes] = useState<EjeDeTiendas[]>([]);
  const [ocupado, setOcupado] = useState(false);
  const [cancelando, setCancelando] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    if (!id) return;
    setCargando(true);
    try {
      const d = await prixiaService.detalle(id);
      setDetalle(d);
      setDatos({
        name: d.tienda.name,
        scraperType: d.tienda.scraperType,
        logoUrl: d.tienda.logoUrl ?? '',
        visitUrl: d.tienda.visitUrl,
        moneda: d.tienda.moneda ?? '',
        ejeId: d.tienda.ejeId ?? '',
      });
      setError(null);
    } catch (err) {
      setError(mensajeDeError(err, 'No se pudo leer la tienda.'));
    } finally {
      setCargando(false);
    }
  }, [id]);

  useEffect(() => {
    void cargar();
    prixiaService
      .lectores()
      .then((l) => {
        setLectores(l);
        setSinLectores(false);
      })
      .catch(() => {
        setLectores([]);
        setSinLectores(true);
      });
    // Sin la lista de rubros, el campo queda en «Sin rubro» y no se puede cambiar: no frena nada más.
    prixiaService
      .ejes()
      .then(setEjes)
      .catch(() => setEjes([]));
  }, [cargar]);

  const fallo = (err: unknown, porDefecto: string) =>
    toast({
      variant: 'destructive',
      title: 'No se pudo',
      description: mensajeDeError(err, porDefecto),
    });

  const accion = async (hacer: () => Promise<void>, porDefecto: string) => {
    setOcupado(true);
    try {
      await hacer();
      await cargar();
    } catch (err) {
      fallo(err, porDefecto);
    } finally {
      setOcupado(false);
    }
  };

  if (error && !detalle) {
    return (
      <AppLayout variante="plataforma">
        <div className="space-y-3 p-6">
          <p className="text-destructive">{error}</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => void cargar()} disabled={cargando}>
              Reintentar
            </Button>
            <Button variant="ghost" onClick={() => navigate('/plataforma/tiendas')}>
              <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" /> Volver a las tiendas
            </Button>
          </div>
        </div>
      </AppLayout>
    );
  }
  if (!detalle || !id) {
    return (
      <AppLayout variante="plataforma">
        <div className="space-y-4 p-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-48" />
        </div>
      </AppLayout>
    );
  }

  const { tienda, salud } = detalle;

  const conTienda = detalle.empresas.filter((e) =>
    e.copias.some((c) => c.isActive && !c.fusionada)
  ).length;

  /** A quiénes alcanza un cambio de la tienda (rige para todas las empresas que la tienen). */
  const rigePara =
    conTienda === 0
      ? 'Ninguna empresa la tiene habilitada todavía.'
      : conTienda === 1
        ? 'Rige para la empresa que la tiene.'
        : `Rige para las ${conTienda} empresas que la tienen.`;

  const alternarEncendido = () => {
    if (tienda.isActive) {
      confirm({
        title: `¿Apagar ${tienda.name}?`,
        description: `${
          conTienda === 0
            ? 'Prixia deja de leerla. Ninguna empresa la tiene habilitada.'
            : conTienda === 1
              ? 'Prixia deja de leerla para la empresa que la tiene.'
              : `Prixia deja de leerla para las ${conTienda} empresas que la tienen.`
        } Lo que espera en la cola se cancela; la lectura que está corriendo termina. No se borra nada de lo leído.`,
        confirmText: 'Sí, apagar',
        variant: 'destructive',
        onConfirm: () =>
          accion(async () => {
            const r = await prixiaService.cambiarEstado(id, false);
            toast({
              title: `${tienda.name}: apagada`,
              description:
                r.canceladas > 0 ? `Se cancelaron ${r.canceladas} lectura(s) en cola.` : undefined,
            });
          }, 'No se pudo apagar la tienda.'),
      });
      return;
    }
    void accion(async () => {
      await prixiaService.cambiarEstado(id, true);
      toast({ title: `${tienda.name}: encendida`, description: 'El calendario la vuelve a leer.' });
    }, 'No se pudo encender la tienda.');
  };

  const pedirLectura = (tipo: TipoDeLectura) =>
    accion(async () => {
      const r = await prixiaService.leer(id, tipo);
      toast({
        title: r.sumada
          ? 'Ya había una lectura en curso o en cola: se sumó a esa'
          : 'Lectura en cola',
        description:
          r.tipo !== tipo
            ? 'Como todavía no tiene un barrido completo, se hace un barrido.'
            : undefined,
      });
    }, 'No se pudo pedir la lectura.');

  /** Un barrido lee todo el catálogo: lo pagan (tráfico) todas las empresas que la tienen. */
  const leer = (tipo: TipoDeLectura) => {
    if (tipo !== 'FULL_RESCRAPE') {
      void pedirLectura(tipo);
      return;
    }
    confirm({
      title: `¿Barrer todo el catálogo de ${tienda.name} ahora?`,
      description: 'Lo pagan todas las empresas que la tienen (tráfico).',
      confirmText: 'Sí, barrer',
      onConfirm: () => {
        void pedirLectura(tipo);
      },
    });
  };

  const guardarDatos = () => {
    const cambio: {
      name?: string;
      scraperType?: string;
      logoUrl?: string | null;
      visitUrl?: string;
      moneda?: string | null;
      ejeId?: string | null;
    } = {};
    if ((datos.ejeId || null) !== (tienda.ejeId ?? null)) cambio.ejeId = datos.ejeId || null;
    if (datos.name.trim() !== tienda.name) cambio.name = datos.name.trim();
    if (datos.scraperType !== tienda.scraperType) cambio.scraperType = datos.scraperType;
    if ((datos.logoUrl.trim() || null) !== tienda.logoUrl)
      cambio.logoUrl = datos.logoUrl.trim() || null;
    if (datos.visitUrl.trim() !== tienda.visitUrl) cambio.visitUrl = datos.visitUrl.trim();
    // Ronda B (punto 19): la moneda en que publica; vacía = no se sabe.
    const moneda = codigoDeMoneda(datos.moneda);
    if (moneda === null) {
      toast({
        title: 'La moneda son las tres letras de su código (USD, EUR, PEN), o vacía',
        variant: 'destructive',
      });
      return;
    }
    if ((moneda || null) !== (tienda.moneda ?? null)) cambio.moneda = moneda || null;
    if (Object.keys(cambio).length === 0) {
      toast({ title: 'No hay cambios para guardar' });
      return;
    }
    const enviar = () =>
      accion(async () => {
        await prixiaService.editar(id, cambio);
        toast({ title: 'Datos guardados' });
      }, 'No se pudieron guardar los datos.');
    if (cambio.scraperType === undefined) {
      void enviar();
      return;
    }
    // Cambiar cómo se lee cambia la próxima lectura de todas las empresas que la tienen.
    confirm({
      title: `¿Cambiar cómo se lee ${tienda.name}?`,
      description: `${rigePara} La próxima lectura ya usa la forma nueva.`,
      confirmText: 'Sí, cambiar',
      onConfirm: () => {
        void enviar();
      },
    });
  };

  const guardarCalendario = (tipo: TipoDeLectura) => async (cambio: CambioDeCalendario) => {
    const enviar = async () => {
      try {
        await prixiaService.cambiarCalendario(id, tipo, cambio);
        toast({
          title: `${TIPO_DE_LECTURA[tipo]}: guardado`,
          description: 'Rige para todas las empresas que tienen la tienda.',
        });
        await cargar();
      } catch (err) {
        fallo(err, 'No se pudo guardar el calendario.');
      }
    };
    // Encenderlo, o leer más seguido, lo pagan (tráfico) todas las empresas que la tienen.
    const antes = detalle.calendario.find((c) => c.type === tipo);
    const seEnciende = cambio.isActive === true && !antes?.isActive;
    const masSeguido =
      antes !== undefined &&
      cambio.intervalValue !== undefined &&
      cambio.intervalUnit !== undefined &&
      enHoras(cambio.intervalValue, cambio.intervalUnit) <
        enHoras(antes.intervalValue, antes.intervalUnit);
    if (!seEnciende && !masSeguido) {
      await enviar();
      return;
    }
    confirm({
      title: `¿Guardar el calendario del ${TIPO_DE_LECTURA[tipo].toLowerCase()}?`,
      description: `${seEnciende ? 'Se enciende' : 'Se va a leer más seguido'}. ${rigePara}`,
      confirmText: 'Sí, guardar',
      onConfirm: () => {
        void enviar();
      },
    });
  };

  const fusionar = (e: EmpresaDeLaTienda) => {
    const vivas = e.copias.filter((c) => !c.fusionada);
    // La misma que deja el servidor (`fusion.ts`): la encendida con más barridos.
    const { queda, encendida } = copiaQueQueda(e);
    const seVan = vivas.filter((c) => c.id !== queda?.id).map((c) => `«${c.name}»`);
    confirm({
      title: `¿Fusionar las copias de ${e.empresa}?`,
      description: `Se queda «${queda?.name ?? ''}» (${
        encendida ? 'la encendida con más barridos' : 'la que más barridos tiene'
      }). Los vínculos de ${seVan.join(', ')} pasan a ella con su historial; esas copias quedan apagadas. No se borra nada.`,
      confirmText: 'Sí, fusionar',
      onConfirm: () =>
        accion(async () => {
          const r = await prixiaService.fusionar(id, e.tenantId);
          toast({
            title: 'Copias fusionadas',
            description: `${r.vinculosMovidos} vínculo(s) pasaron a «${r.queda.name}»${
              r.vinculosArchivados > 0
                ? `; ${r.vinculosArchivados} repetido(s) quedaron archivados`
                : ''
            }.`,
          });
        }, 'No se pudieron fusionar las copias.'),
    });
  };

  const cancelar = (l: FilaDeLecturaPrixia) => {
    const hacer = async () => {
      setCancelando(l.id);
      try {
        await prixiaService.cancelarLectura(l.id);
        toast({ title: 'Lectura cancelada' });
        await cargar();
      } catch (err) {
        fallo(err, 'No se pudo cancelar la lectura.');
      } finally {
        setCancelando(null);
      }
    };
    if (l.status !== 'RUNNING') {
      void hacer();
      return;
    }
    confirm({
      title: '¿Cancelar la lectura?',
      description:
        'La lectura ya está leyendo para todas las empresas que tienen la tienda; lo leído hasta ahora no se reparte.',
      confirmText: 'Sí, cancelar',
      variant: 'destructive',
      onConfirm: () => {
        void hacer();
      },
    });
  };

  return (
    <AppLayout variante="plataforma">
      <ConfirmDialog />
      <div className="space-y-6 p-6">
        <div className="flex flex-wrap items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/plataforma/tiendas')}>
            <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" /> Volver
          </Button>
          <div className="flex-1">
            <h1 className="flex items-center gap-2 text-2xl font-bold">
              <Store className="h-6 w-6" aria-hidden="true" />
              {tienda.name}
            </h1>
            <p className="text-sm text-muted-foreground">
              {`${tienda.url} · ${tienda.publicados.toLocaleString('es-BO')} artículos publicados de ${tienda.articulos.toLocaleString('es-BO')} vistos`}
            </p>
            {/* Un <div> y no un <p>: la etiqueta (Badge) es un <div>, y un <div> dentro
                de un <p> no es HTML válido (React lo avisa). */}
            <div className="mt-1 text-sm">
              <Badge variant={SALUD[salud.estado].variante}>{SALUD[salud.estado].texto}</Badge>
              <span className="ml-2 text-muted-foreground">{salud.frase}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              disabled={ocupado || !tienda.isActive}
              onClick={() => leer('PRICE_CHECK')}
            >
              <DollarSign className="mr-2 h-4 w-4" aria-hidden="true" />
              Chequear ahora
            </Button>
            <Button
              variant="outline"
              disabled={ocupado || !tienda.isActive}
              onClick={() => leer('FULL_RESCRAPE')}
            >
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
              Barrer ahora
            </Button>
            <Button
              variant={tienda.isActive ? 'destructive' : 'default'}
              disabled={ocupado}
              onClick={alternarEncendido}
              aria-label={`${tienda.isActive ? 'Apagar' : 'Encender'} ${tienda.name}`}
            >
              {tienda.isActive ? (
                <>
                  <Ban className="mr-2 h-4 w-4" aria-hidden="true" />
                  Apagar
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" aria-hidden="true" />
                  Encender
                </>
              )}
            </Button>
          </div>
        </div>

        {error && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3">
            <p className="text-sm text-destructive">
              {`${error} Lo que se ve puede no estar al día.`}
            </p>
            <Button variant="outline" size="sm" onClick={() => void cargar()} disabled={cargando}>
              Reintentar
            </Button>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Calendario de lecturas</CardTitle>
            <p className="text-sm text-muted-foreground">
              Rige para todas las empresas que tienen la tienda. Una empresa con «Monitoreo de
              lecturas» también puede cambiar cada cuánto se lee, dentro de los mínimos de la
              plataforma («Lecturas»).
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {TIPOS.map((tipo) => (
              <EditorDeCalendarioPrixia
                key={tipo}
                tipo={tipo}
                calendario={detalle.calendario.find((c) => c.type === tipo)}
                tiendaActiva={tienda.isActive}
                onGuardar={guardarCalendario(tipo)}
              />
            ))}
            {detalle.cambiosDeCalendario.length > 0 && (
              <div className="pt-2">
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Últimos cambios
                </p>
                <ul className="space-y-1 text-xs text-muted-foreground">
                  {detalle.cambiosDeCalendario.map((c, i) => (
                    <li key={`${c.createdAt}-${i}`}>
                      {`${fechaYHora(c.createdAt)} · ${TIPO_DE_LECTURA[c.type]}: ${frecuenciaEnTexto(c.antes)} → ${frecuenciaEnTexto(c.despues)} · ${c.quien}`}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Datos de la tienda</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="tienda-prixia-nombre">Nombre</Label>
              <Input
                id="tienda-prixia-nombre"
                value={datos.name}
                onChange={(e) => setDatos({ ...datos, name: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tienda-prixia-lector">Cómo se lee</Label>
              <SelectNativo
                id="tienda-prixia-lector"
                className="w-full"
                value={datos.scraperType}
                onChange={(e) => setDatos({ ...datos, scraperType: e.target.value })}
              >
                {etiquetasDeLectores(
                  lectores.some((l) => l.type === datos.scraperType)
                    ? lectores
                    : // El que usa hoy, aunque no haya venido en la lista (sin su código).
                      [...lectores, { type: datos.scraperType, name: datos.scraperType }]
                ).map((l) => (
                  <option key={l.type} value={l.type}>
                    {l.name}
                  </option>
                ))}
              </SelectNativo>
              {sinLectores && (
                <p className="text-xs text-amber-600">
                  No se pudo leer la lista de lectores: se puede guardar con la que usa ahora.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tienda-prixia-visita">Dirección con la que se visita</Label>
              <Input
                id="tienda-prixia-visita"
                value={datos.visitUrl}
                onChange={(e) => setDatos({ ...datos, visitUrl: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                {`Tiene que seguir siendo ${tienda.url}: otra dirección es otra tienda.`}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tienda-prixia-rubro">Rubro para la prueba</Label>
              <SelectNativo
                id="tienda-prixia-rubro"
                className="w-full"
                value={datos.ejeId}
                onChange={(e) => setDatos({ ...datos, ejeId: e.target.value })}
              >
                <option value="">Sin rubro (Otras tiendas)</option>
                {ejes
                  .filter((e) => e.activo || e.id === datos.ejeId)
                  .map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.activo ? e.nombre : `${e.nombre} (apagado)`}
                    </option>
                  ))}
              </SelectNativo>
              <p className="text-xs text-muted-foreground">
                Las empresas en prueba la ven en «De tu rubro» si eligen este rubro; si no, en
                «Otras tiendas». Si era la sugerida de otro rubro, deja de serlo.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tienda-prixia-moneda">Moneda en que publica sus precios</Label>
              <Input
                id="tienda-prixia-moneda"
                className="w-28 uppercase"
                maxLength={3}
                value={datos.moneda}
                onChange={(e) => setDatos({ ...datos, moneda: e.target.value })}
                placeholder="USD"
              />
              <p className="text-xs text-muted-foreground">
                {tienda.moneda
                  ? 'El chequeo diario no toma un precio publicado en otra moneda.'
                  : 'Todavía no se sabe. Con la moneda, el chequeo diario no toma un precio publicado en otra.'}
              </p>
              <p className="text-xs text-muted-foreground">
                Si la tienda cambia sus precios según el país de quien la visita, elegí la moneda en
                que publica para su mercado. Si una página llega en otra moneda, ese precio queda
                sin dato.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tienda-prixia-logo">Logo</Label>
              <Input
                id="tienda-prixia-logo"
                value={datos.logoUrl}
                onChange={(e) => setDatos({ ...datos, logoUrl: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <Button onClick={guardarDatos} disabled={ocupado}>
                Guardar datos
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Empresas que la tienen</CardTitle>
            <p className="text-sm text-muted-foreground">
              Se habilita o se quita desde la ficha de cada empresa («Tiendas disponibles»).
            </p>
          </CardHeader>
          <CardContent className="p-0">
            {detalle.empresas.length === 0 ? (
              <p className="p-6 text-center text-sm text-muted-foreground">
                Ninguna empresa la tiene habilitada.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Su copia</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Vínculos</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detalle.empresas.map((e) => (
                    <TableRow key={e.tenantId}>
                      <TableCell className="font-medium">
                        <button
                          type="button"
                          className="hover:underline"
                          onClick={() => navigate(`/plataforma/empresas/${e.tenantId}`)}
                        >
                          {e.empresa}
                        </button>
                      </TableCell>
                      <TableCell className="text-sm">
                        {e.copias.map((c) => (
                          <div key={c.id}>
                            {c.name}
                            {c.fusionada && (
                              <span className="ml-1 text-xs text-muted-foreground">
                                (fusionada)
                              </span>
                            )}
                            {!c.fusionada && !c.includeInAnalysis && (
                              <span className="ml-1 text-xs text-muted-foreground">
                                (fuera del análisis)
                              </span>
                            )}
                          </div>
                        ))}
                      </TableCell>
                      <TableCell>
                        {e.copias.some((c) => c.isActive) ? (
                          <Badge variant="success">Habilitada</Badge>
                        ) : (
                          <Badge variant="outline">Quitada</Badge>
                        )}
                        {e.repetida && (
                          <Badge variant="warning" className="ml-2">
                            Copia repetida
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {e.copias.reduce((s, c) => s + c.vinculos, 0).toLocaleString('es-BO')}
                      </TableCell>
                      <TableCell className="text-right">
                        {e.repetida && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={ocupado}
                            onClick={() => fusionar(e)}
                          >
                            Fusionar copias
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Últimas lecturas</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <TablaDeLecturasPrixia
              lecturas={detalle.lecturas}
              onCancelar={cancelar}
              ocupado={cancelando !== null}
              vacio="Todavía no hay lecturas de esta tienda."
            />
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}

export default AdminTiendaPrixiaPage;
